import { Router } from '@angular/router';
import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, BehaviorSubject } from 'rxjs';
import { MingoErrorsHandler } from './errors-handler.service';
import { BaseURL, vaySettings } from '../vay.config';
import { map, catchError } from 'rxjs/operators';
import { VayAntService } from './vay-ant.service';
import { CpanelService } from './cpanel.service';
import { LanguageService } from './language.service';
import { SettingService } from './setting.service';

@Injectable()
export class UsersService {
  isAuthenticated = false;
  user = new BehaviorSubject<any>({});
  userData: any;
  currentUser = this.user.asObservable();
  userRoles = new BehaviorSubject(null as any);

  constructor(
    private https: HttpClient,
    private mingoErrorsHandler: MingoErrorsHandler,
    private router: Router,
    private antService: VayAntService,
    private cpanelService: CpanelService,
    private languageService: LanguageService,
    private settingService: SettingService
  ) {}

  checkJWTtoken(redirect = true) {
    const user = this.getUserFromStorage();
    if (user) {
      this.https.get<any>(`${BaseURL}/api/user/checkToken`).subscribe(
        (res: any) => {
          this.user.next(res.data);
          this.userData = res.data;
          this.isAuthenticated = true;
        },
        (err) => {
          this.logout();
        }
      );
    }
  }

  logout() {
    localStorage.removeItem('user');
    this.user.next(null);
    this.router.navigateByUrl('/login');
    this.cpanelService.getCountryConfig().subscribe((res: any) => {
      this.languageService.availableLanguages.next(res.data.countryLangs);
      this.languageService.country.next(res.data);
      this.settingService.getPlans(res.data._id);
    });
  }

  logIn(user: any): Observable<any> {
    return this.https.post<any>(BaseURL + '/api/user/login', user).pipe(
      map((res: any) => {
        localStorage.setItem('user', JSON.stringify(res));
        this.userData = res;
        this.user.next(res);
        this.isAuthenticated = true;
        if (res.role == 'resturant') {
          this.router.navigate([vaySettings.info.defaultRouter]);
        } else {
          this.router.navigate(['/admin-dashboard']);
          this.checkCurrentUserRole();
        }
        this.cpanelService.getCountryConfig().subscribe((res: any) => {
          this.languageService.availableLanguages.next(res.data.countryLangs);
          this.languageService.country.next(res.data);
          this.settingService.getPlans(res.data._id);
        });
        return res;
      }),
      catchError((e: any) => this.mingoErrorsHandler.handleError(e))
    );
  }

  signupUser(user: any) {
    return this.https.post(`${BaseURL}/api/user/join`, user).pipe(
      map((res: any) => {
        localStorage.setItem('user', JSON.stringify(res));
        this.userData = res;
        this.user.next(res);
        this.isAuthenticated = true;
        this.checkJWTtoken();
        this.cpanelService.getCountryConfig().subscribe((res: any) => {
          this.languageService.availableLanguages.next(res.data.countryLangs);
          this.languageService.country.next(res.data);
          this.settingService.getPlans(res.data._id);
        });
        return res;
      }),
      catchError((e: any) => this.mingoErrorsHandler.handleError(e))
    ) as Observable<any>;
  }

  getToken() {
    const user = this.getUserFromStorage();
    return user ? user.token : '';
  }

  getUserFromStorage() {
    const temp = localStorage.getItem('user');
    return temp ? JSON.parse(temp) : null;
  }

  isUser() {
    const user = this.getUserFromStorage();
    if (!user) {
      return false;
    }

    return user.role.includes('resturant') ? true : false;
  }

  isAdmin() {
    const user = this.getUserFromStorage();
    if (!user) {
      return false;
    }

    return user.role.includes('admin') ? true : false;
  }

  isOwner() {
    const user = this.getUserFromStorage();
    if (!user) {
      return false;
    }

    return user.user.owner;
  }

  checkCurrentUserRole() {
    if (this.isOwner()) {
      this.userRoles.next({
        roles: ['admin'],
      });
    } else {
      this.userRoles.next({
        roles: [],
      });
    }
  }

  getUsers(_params: any) {
    let params = new HttpParams();
    Object.keys(_params).map((key: any) => {
      params = params.append(key, _params[key] != null ? _params[key] : '');
    });
    return this.https
      .get(`${BaseURL}/api/user/users`, { params })
      .pipe(catchError((e: any) => this.mingoErrorsHandler.handleError(e))) as Observable<any>;
  }

  getDrivers(_params: any) {
    let params = new HttpParams();
    Object.keys(_params).map((key: any) => {
      params = params.append(key, _params[key] != null ? _params[key] : '');
    });
    return this.https
      .get(`${BaseURL}/api/driver/getAllDriversForResturant`, { params })
      .pipe(catchError((e: any) => this.mingoErrorsHandler.handleError(e))) as Observable<any>;
  }

  getDriversForAdmin(_params: any) {
    let params = new HttpParams();
    Object.keys(_params).map((key: any) => {
      params = params.append(key, _params[key] != null ? _params[key] : '');
    });
    return this.https
      .get(`${BaseURL}/api/driver/GetAllDriversByAdmin`, { params })
      .pipe(catchError((e: any) => this.mingoErrorsHandler.handleError(e))) as Observable<any>;
  }

  getUsersDashboard(_params: any) {
    let params = new HttpParams();
    Object.keys(_params).map((key: any) => {
      params = params.append(key, _params[key] != null ? _params[key] : '');
    });
    return this.https
      .get(`${BaseURL}/api/user/admins`, { params })
      .pipe(catchError((e: any) => this.mingoErrorsHandler.handleError(e))) as Observable<any>;
  }

  activeAndDisactiveUser(id: string): Observable<any> {
    return this.https.put(BaseURL + `/api/user/${id}/active`, {}).pipe(
      map((res: any) => {
        return res;
      }),
      catchError((e: any) => this.mingoErrorsHandler.handleError(e))
    );
  }

  getUser(id: string): Observable<any> {
    return this.https.get(BaseURL + `/api/user/user/${id}`);
  }

  getDriver(id: string): Observable<any> {
    return this.https.get(BaseURL + `/api/driver/getOneDriver/${id}`);
  }

  getUserDashboard(id: string): Observable<any> {
    return this.https.get(BaseURL + `/api/user/admins/${id}`);
  }
  deleteWaiterOrCook(id: string): Observable<any> {
    return this.https.delete(BaseURL + `/api/user/deleteWaiterOrCook/${id}`);
  }
  deleteDriver(id: string): Observable<any> {
    return this.https.delete(BaseURL + `/api/user/deleteDriver/${id}`);
  }
  deleteDriverForRestaurant(id: string): Observable<any> {
    return this.https.delete(BaseURL + `/api/driver/removeDriverFromResturant/${id}`);
  }

  getPointsLoction(): Observable<any> {
    return this.https.get(BaseURL + `/api/user/resturants/map`);
  }

  getProfile(): Observable<any> {
    return this.https.get(BaseURL + `/api/user/currentuser`);
  }

  GetAllUsersDropdown(): Observable<any> {
    return this.https.get(BaseURL + '/api/users/GetAllUsersDropdown/');
  }

  GetAllRestaurants(): Observable<any> {
    return this.https.get(BaseURL + '/api/users/GetAllUsersDropdown/');
  }

  hasRoles(roles: string[]): boolean {
    for (const oneRole of roles) {
      if (!this.userRoles.value || !this.userRoles.value.roles.includes(oneRole)) {
        return false;
      }
    }
    return true;
  }

  addUserByAdminDashboard(user: any) {
    return this.https.post(`${BaseURL}/api/user/admin/add`, user).pipe(
      map((res: any) => {
        this.antService.showMessage(res.message || 'done !');
        return res;
      }),
      catchError((e: any) => this.mingoErrorsHandler.handleError(e))
    ) as Observable<any>;
  }

  addUserByAdmin(user: any) {
    return this.https.post(`${BaseURL}/api/user/addWaiterOrCook`, user).pipe(
      map((res: any) => {
        this.antService.showMessage(res.message || 'done !');
        return res;
      }),
      catchError((e: any) => this.mingoErrorsHandler.handleError(e))
    ) as Observable<any>;
  }

  editUserByAdmin(id: any, user: any) {
    return this.https.put(`${BaseURL}/api/user/editWaiterOrCook/${id}`, user).pipe(
      map((res: any) => {
        this.antService.showMessage(res.message || 'done !');
        return res;
      }),
      catchError((e: any) => this.mingoErrorsHandler.handleError(e))
    ) as Observable<any>;
  }

  addDriver(user: any) {
    return this.https.post(`${BaseURL}/api/driver/addDriverForResturant`, user).pipe(
      map((res: any) => {
        this.antService.showMessage(res.message || 'done !');
        return res;
      }),
      catchError((e: any) => this.mingoErrorsHandler.handleError(e))
    ) as Observable<any>;
  }

  addDriverToRestaurant(user: any) {
    return this.https.post(`${BaseURL}/api/driver/addDriverFromResturant/${user}`, {}).pipe(
      map((res: any) => {
        this.antService.showMessage(res.message || 'done !');
        return res;
      }),
      catchError((e: any) => this.mingoErrorsHandler.handleError(e))
    ) as Observable<any>;
  }

  editDriver(id: any, user: any) {
    return this.https.put(`${BaseURL}/api/driver/editDriverForResturant/${id}`, user).pipe(
      map((res: any) => {
        this.antService.showMessage(res.message || 'done !');
        return res;
      }),
      catchError((e: any) => this.mingoErrorsHandler.handleError(e))
    ) as Observable<any>;
  }

  editDriverByAdmin(id: any, user: any) {
    return this.https.put(`${BaseURL}/api/driver/editResturantsForDriver/${id}`, user).pipe(
      map((res: any) => {
        this.antService.showMessage(res.message || 'done !');
        return res;
      }),
      catchError((e: any) => this.mingoErrorsHandler.handleError(e))
    ) as Observable<any>;
  }

  editUserByAdminDashboard(id: any, user: any) {
    return this.https.put(`${BaseURL}/api/user/editAdmin/${id}`, user).pipe(
      map((res: any) => {
        this.antService.showMessage(res.message || 'done !');
        return res;
      }),
      catchError((e: any) => this.mingoErrorsHandler.handleError(e))
    ) as Observable<any>;
  }

  editProfile(data: any): Observable<any> {
    return this.https.put<any>(BaseURL + '/api/user/updateProfile', data).pipe(
      map((res: any) => {
        this.antService.showMessage(res.message || 'done !');
        return res;
      }),
      catchError((e: any) => this.mingoErrorsHandler.handleError(e))
    ) as Observable<any>;
  }

  editPassword(data: any): Observable<any> {
    return this.https.put<any>(BaseURL + '/api/user/updatePassword', data).pipe(
      map((res: any) => {
        this.antService.showMessage(res.message || 'done !');
        return res;
      }),
      catchError((e: any) => this.mingoErrorsHandler.handleError(e))
    ) as Observable<any>;
  }

  forgetPassword(data: any): Observable<any> {
    return this.https.post<any>(BaseURL + '/api/user/forgotpassword', data).pipe(
      map((res: any) => {
        this.antService.showMessage(res.message || 'done !');
        return res;
      }),
      catchError((e: any) => this.mingoErrorsHandler.handleError(e))
    ) as Observable<any>;
  }

  editMyInfo(data: any): Observable<any> {
    return this.https.post<any>(BaseURL + '/api/users/resetuser/', data).pipe(
      map((res: any) => {
        this.antService.showMessage(res.message || 'done !');
        return res;
      }),
      catchError((e: any) => this.mingoErrorsHandler.handleError(e))
    ) as Observable<any>;
  }

  // addNewUser(user) {
  //   return this.http.post(`${BaseURL}/api/users/signup/`, user).pipe(
  //     map((res: any) => {
  //       this.storeUserCredentials({ username: user.email, token: res.token, role: res.role });
  //       this.elzero.showSuccess(res.message || 'done !');
  //       return res;
  //     }),
  //     catchError(e => this.mingoErrorsHandler.handleError(e))
  //   ) as Observable<any>;
  // }

  // logOut() {
  //   // this.destroyUserCredentials();
  // }

  // verifyNewAccount(id) {
  //   return this.https.post(`${BaseURL}/api/users/verifyNewAccount/${id}`, {}).pipe(
  //     map((res: any) => {
  //       this.elzero.showSuccess(res.message || 'done !');
  //       return res;
  //     }),
  //     catchError(e => this.mingoErrorsHandler.handleError(e))
  //   ) as Observable<any>;
  // }
}
